import { datadogRum } from '@datadog/browser-rum'
import { ApolloError } from '@apollo/client'
import { DefaultModalContainerNames } from '../../consts/defaultConfigValues/defaultConstants'
import useActivateDeferredPromotion from '../../graphQL/mutations/activateDeferredPromotion/hook'
import {
  IActivateDeferredPromotionResponse,
  IActivateDeferredPromotionData,
} from '../../graphQL/mutations/activateDeferredPromotion/types'
import {
  setAvailableFreegoods,
  setQuantitySelection,
  setSynchronizingCart,
} from '../../redux/slices/catalog'
import {
  getAlertErrorMessage,
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import { onCartResponse } from '../../utils/onCartResponse'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useModal from '../useModal'
import { addAlert } from '../../redux/slices/config'
import { generateDataDogContext } from '../../utils/dataDog'
import { PromotionSelectionCriteria } from '../../graphQL/commons'

export const useChosenFreeGoods = () => {
  const { closeModal } = useModal()
  const dispatch = useAppDispatch()

  const availableFreegoods = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.freegoods || [],
  )

  const promotionUid = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.promotion?.id || '',
  )
  const state = useAppSelector((stateRedux) => stateRedux)
  const { config } = state.defaultSlice
  const sessionUid = useAppSelector(
    (stateRedux) => stateRedux.defaultSlice.sessionId || '',
  )
  const storefrontName = useAppSelector(
    (stateRedux) => stateRedux.defaultSlice.storeName || '',
  )
  const selectByState = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.selectBy,
  )
  const quantitySelection = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.quantitySelection,
  )

  const activeModalContainer =
    selectByState === PromotionSelectionCriteria.QUANTITY_SELECTION
      ? DefaultModalContainerNames.CHOSEN_FREEGOODS_QUANTITY
      : DefaultModalContainerNames.CHOSEN_FREEGOODS

  const onCompleted = (data: IActivateDeferredPromotionResponse) => {
    if (data.activateDeferredPromotion) {
      onCartResponse({
        data: data.activateDeferredPromotion,
        state,
        dispatch,
      })
      closeModal({ container: activeModalContainer })
    }
  }

  const onError = (error: ApolloError) => {
    const message = getAlertErrorMessage(
      error.networkError ? AlertMessageType.Network : AlertMessageType.AddItem,
      config?.texts,
    )
    dispatch(addAlert({ message }))
    const context = generateDataDogContext({
      title: 'Add product mutation error',
      extraInfo: { function: 'useAddItemToCart > onError' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const [activateDeferredPromotion] = useActivateDeferredPromotion({
    onError,
    onCompleted,
  })

  const chosenFreegoods = ({ id }: { id: string }) => {
    try {
      dispatch(setSynchronizingCart(true))

      const newAvailableFreegoods = availableFreegoods.map((item) => ({
        ...item,
        isChecked: item.id === id,
      }))
      dispatch(
        setAvailableFreegoods({
          freegoods: newAvailableFreegoods,
          isConfirmDisabled: !newAvailableFreegoods.some(
            ({ isChecked }) => isChecked,
          ),
        }),
      )
    } finally {
      dispatch(setSynchronizingCart(false))
    }
  }

  const confirmChosenFreegoods = async ({
    selectBy,
  }: {
    selectBy: PromotionSelectionCriteria
  }) => {
    const selectedGroup = availableFreegoods.find((item) => item.isChecked)

    const selectedSkuQuantities =
      quantitySelection?.products
        ?.filter(({ quantity }) => quantity > 0)
        .map(({ sku, quantity }) => ({
          sku,
          quantity,
        })) || []

    const requiresCustomerSelection =
      selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION

    if (requiresCustomerSelection && !selectedGroup) {
      return
    }

    const requiresQuantitySelection =
      selectBy === PromotionSelectionCriteria.QUANTITY_SELECTION

    if (requiresQuantitySelection && selectedSkuQuantities.length === 0) {
      return
    }

    const selectedGroupIdentifier = (() => {
      if (requiresCustomerSelection) {
        return selectedGroup?.id ?? null
      }
      if (requiresQuantitySelection) {
        return quantitySelection?.groups?.[0]?.identifier ?? null
      }
      return null
    })()

    const promotionData: IActivateDeferredPromotionData = {
      selectBy,
      promotionUid,
      selectedGroupIdentifier: selectedGroupIdentifier || undefined,
    }

    if (requiresQuantitySelection && selectedSkuQuantities.length > 0) {
      promotionData.skuQuantitySelections = selectedSkuQuantities
    }

    await activateDeferredPromotion({
      variables: {
        sessionUid,
        storefrontName,
        data: promotionData,
      },
    })
  }

  const updateQuantitySelection = ({
    sku,
    quantity,
  }: {
    sku: string
    quantity: number
  }) => {
    if (!quantitySelection) {
      return
    }

    const { products: quantitySelectionProducts, maxTotalQuantity } =
      quantitySelection

    const updatedQuantitySelectionProducts = quantitySelectionProducts.map(
      (product) => {
        if (product.sku === sku) {
          return {
            ...product,
            quantity,
          }
        }
        return product
      },
    )

    const totalSelected = updatedQuantitySelectionProducts.reduce(
      (acc, { quantity }) => acc + quantity,
      0,
    )

    const updatedQuantitySelection = {
      ...quantitySelection,
      products: updatedQuantitySelectionProducts,
      totalSelected,
    }

    dispatch(
      setQuantitySelection({
        quantitySelection: updatedQuantitySelection,
        isConfirmDisabled: totalSelected !== maxTotalQuantity,
      }),
    )
  }

  return { chosenFreegoods, confirmChosenFreegoods, updateQuantitySelection }
}
